/** A KPI block saved to the backend via POST /api/v1/dashboard/blocks */
export interface SavedBlock {
  blockId: string;
  type: string;
  title: string;
  payload: Record<string, unknown>;
  dataRef?: DataRef;
  organizationId: string;
  position?: number;
  createdAt: string;
  updatedAt: string;
}

/** Reference to a live data entity for real-time updates via GraphQL subscription. */
export interface DataRef {
  entity: string;
  id: string;
  fields?: string[];
}

export interface KpiBlock {
  type: 'kpi';
  id: string;
  title: string;
  value: number | string;
  delta?: string | null;
  unit?: string | null;
  trend?: string | null;
  change_pct?: number | null;
  source?: string | null;
  timeframe?: string | null;
  provenance?: string | null;
  data_ref?: DataRef;
}

/** Validate and extract a DataRef from a raw block field, or return undefined. */
export function normalizeDataRef(raw: unknown): DataRef | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const ref = raw as Record<string, unknown>;
  const entity = ref.entity;
  const id = ref.id;
  if (typeof entity !== 'string' || !entity) return undefined;
  if (typeof id !== 'string' || !id) return undefined;
  return {
    entity,
    id,
    fields: Array.isArray(ref.fields)
      ? ref.fields.filter((f): f is string => typeof f === 'string')
      : undefined,
  };
}

/**
 * Normalize a raw ui_block into a KpiBlock, or return null if invalid.
 */
export function normalizeKpiBlock(raw: unknown): KpiBlock | null {
  if (!raw || typeof raw !== 'object') return null;

  const block = raw as Record<string, unknown>;
  const rawType = String(block.type ?? '');

  // Accept 'kpi' or 'kpi_card'
  if (rawType !== 'kpi' && rawType !== 'kpi_card') return null;

  const title = String(block.title ?? '').trim();
  const value = block.value;
  if (!title || value === undefined || value === null) return null;

  return {
    type: 'kpi',
    id: String(block.id ?? `kpi-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`),
    title,
    value: typeof value === 'number' ? value : String(value),
    delta: block.delta != null ? String(block.delta) : null,
    unit: block.unit != null ? String(block.unit) : null,
    trend: block.trend != null ? String(block.trend) : null,
    change_pct: typeof block.change_pct === 'number' ? block.change_pct : null,
    source: block.source != null ? String(block.source) : null,
    timeframe: block.timeframe != null ? String(block.timeframe) : null,
    provenance: block.provenance != null ? String(block.provenance) : null,
    data_ref: normalizeDataRef(block.data_ref),
  };
}

/**
 * Extract all valid KPI blocks from a raw ui_blocks array.
 */
export function normalizeKpiBlocks(rawBlocks: unknown[] | undefined): KpiBlock[] {
  if (!rawBlocks || !Array.isArray(rawBlocks)) return [];
  return rawBlocks.map(normalizeKpiBlock).filter((b): b is KpiBlock => b !== null);
}
